import { ServerDate } from '@tablesready/server-time';
import { FormsModule } from '@angular/forms';

import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, ToastController } from '@ionic/angular';

import { Angulartics2RouterlessModule } from 'angulartics2';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { of, switchMap, take, tap } from 'rxjs';

import {
  CommonUiModule,
  GlobalErrorHandler,
  PageTitleService,
  ServerTimeService,
} from '@tablesready/common-ui';
import { ApiClientModule } from '@tablesready/api-client';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { PROVIDERS } from './providers';
import {
  AuthenticatedLayoutComponent,
  ChatPartyModule,
} from './components';
import { ForbiddenHttpInterceptor } from './auth/services';
import { environment } from '../environments/environment';
import { FrontendModalModule } from '@tablesready/frontend-modal';
import { LocationSwitcherModule } from '@tablesready/frontend-location-switcher';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { FrontendHeaderModule } from '@tablesready/frontend-header';
import { ThemeSwitchModule } from '@tablesready/frontend-theme-switch';
import { AuthModule, AuthService } from '@tablesready/frontend-auth';
import {
  AppVariables,
  appVariablesProvider,
  AppVariablesService,
} from '@tablesready/frontend-variables';
import { FrontendSettingsNavModule } from '@tablesready/frontend-settings-nav';
import { StorageModule } from '@tablesready/frontend-storage';
import { AlertsModule } from '@tablesready/frontend-alerts-bar';
import { ErrorInterceptor } from '@tablesready/common-ui';
import { FrontendGooglePlacesModule } from '@tablesready/frontend-google-places';

@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    AuthenticatedLayoutComponent,
  ],
  imports: [
    ApiClientModule,
    AuthModule,
    ThemeSwitchModule,
    BrowserModule,
    FrontendModalModule,
    IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
    BrowserAnimationsModule,
    CommonUiModule,
    AppRoutingModule,
    AngularSvgIconModule.forRoot(),
    Angulartics2RouterlessModule.forRoot(),
    FormsModule,
    StorageModule.forRoot(),
    LocationSwitcherModule,
    FrontendHeaderModule,
    ChatPartyModule,
    NgbDropdownModule,
    NgbTooltipModule,
    FrontendSettingsNavModule,
    AlertsModule.forRoot(),
    FrontendGooglePlacesModule,
  ],
  providers: [
    appVariablesProvider,
    AppVariablesService,
    {
      provide: 'LOG_ERRORS',
      useValue: environment.logErrors,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (appVariablesService: AppVariablesService): () => Promise<AppVariables> => async (): Promise<AppVariables> => appVariablesService
        .getAppVariables()
        .toPromise()
        .catch(),
      multi: true,
      deps: [AppVariablesService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (
        authService: AuthService,
        authRestaurantService: AuthRestaurantService,
      ) => () => of(authService.isAuthenticated()).pipe(
        switchMap((isAuthenticated) => {
          if (isAuthenticated) {
            return authRestaurantService.load()
              .pipe(switchMap(() => authRestaurantService.buildOnce()));
          }

          return of(null);
        }),
        take(1),
      ),
      multi: true,
      deps: [AuthService, AuthRestaurantService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (serverTimeService: ServerTimeService) => () => serverTimeService.offset$
        .pipe(
          tap((offset) => {
            ServerDate.setOffset(offset);
          }),
          take(1),
        ),
      multi: true,
      deps: [ServerTimeService],
    },
    PageTitleService,
    ...PROVIDERS,
    ToastController,
  ],
})
export class AppModule { }
